/**
 * PolicyViolationAlert Component
 * 
 * Displays policy violations with appropriate styling and severity indicators.
 * Automatically consolidates multiple violations of the same type to reduce redundancy.
 */

import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import type { PolicyViolation } from '@/data/mockPolicyViolations';
import type { PolicyCheckType } from '@/types/timesheetSettings';
import { consolidatePolicyViolations, shouldConsolidate, VIOLATION_TYPE_PRIORITY } from '@/services/policyViolationConsolidationService';

interface PolicyViolationAlertProps {
  violations: PolicyViolation[];
  compact?: boolean;
}

const getViolationIcon = (severity: 'info' | 'warning' | 'error') => {
  switch (severity) {
    case 'error':
      return <AlertCircle className="h-4 w-4" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4" />;
    case 'info':
      return <Info className="h-4 w-4" />;
  }
};

const getViolationVariant = (severity: 'info' | 'warning' | 'error') => {
  switch (severity) {
    case 'error':
      return 'destructive';
    case 'warning':
      return 'default';
    case 'info':
      return 'default';
  }
};

const getViolationTypeLabel = (type: PolicyCheckType): string => {
  switch (type) {
    case 'daily-overage':
      return 'Daily Overage';
    case 'weekly-overage':
      return 'Weekly Overage';
    case 'legal-daily-maximum':
      return 'Legal Maximum (Daily)';
    case 'legal-weekly-maximum':
      return 'Legal Maximum (Weekly)';
    case 'missing-hours':
      return 'Missing Hours';
    case 'non-working-day':
      return 'Non-Working Day';
    default:
      return type;
  }
};

const getSeverityBadgeVariant = (severity: 'info' | 'warning' | 'error') => {
  switch (severity) {
    case 'error':
      return 'destructive';
    case 'warning':
      return 'default';
    case 'info':
      return 'secondary';
  }
};

export function PolicyViolationAlert({ violations, compact = false }: PolicyViolationAlertProps) {
  if (violations.length === 0) {
    return null;
  }

  // Group violations by severity
  const errorViolations = violations.filter(v => v.severity === 'error');
  const warningViolations = violations.filter(v => v.severity === 'warning');
  const infoViolations = violations.filter(v => v.severity === 'info');

  if (compact) {
    // Compact view - just show counts
    return (
      <div className="flex items-center gap-2">
        {errorViolations.length > 0 && (
          <Badge variant="destructive" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            {errorViolations.length}
          </Badge>
        )}
        {warningViolations.length > 0 && (
          <Badge variant="default" className="gap-1">
            <AlertTriangle className="h-3 w-3" />
            {warningViolations.length}
          </Badge>
        )}
        {infoViolations.length > 0 && (
          <Badge variant="secondary" className="gap-1">
            <Info className="h-3 w-3" />
            {infoViolations.length}
          </Badge>
        )}
      </div>
    );
  }

  // Check if consolidation should be applied
  const useConsolidation = shouldConsolidate(violations);
  
  if (useConsolidation) {
    // Consolidate violations by type
    const consolidated = consolidatePolicyViolations(violations);
    
    return (
      <div className="space-y-3">
        {consolidated.map((group) => (
          <Alert
            key={group.id}
            variant={getViolationVariant(group.severity)}
            className="relative"
            showIcon={false}
          >
            <div className="flex items-start gap-3">
              {getViolationIcon(group.severity)}
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTitle className="text-sm font-medium">
                    {group.summary}
                  </AlertTitle>
                  <Badge variant={getSeverityBadgeVariant(group.severity)} className="text-xs">
                    {group.severity.toUpperCase()}
                  </Badge>
                </div>
                
                {/* List of violations */}
                <div className="space-y-1">
                  {group.details.map((detail, index) => (
                    <div key={index} className="text-sm">
                      {detail.day && <span className="font-medium">{detail.day}: </span>}
                      {detail.message}
                    </div>
                  ))}
                </div>
                
                {/* Insight message */}
                <AlertDescription className="text-sm mt-2 pt-2 border-t">
                  {group.insight}
                </AlertDescription>
              </div>
            </div>
          </Alert>
        ))}
      </div>
    );
  }

  // Sort violations by severity then type priority
  const sortedViolations = [...violations].sort((a, b) => {
    const severityOrder: Record<string, number> = { error: 0, warning: 1, info: 2 };
    const severityDiff = (severityOrder[a.severity] ?? 2) - (severityOrder[b.severity] ?? 2);
    if (severityDiff !== 0) return severityDiff;
    const priorityA = VIOLATION_TYPE_PRIORITY[a.violationType] ?? 99;
    const priorityB = VIOLATION_TYPE_PRIORITY[b.violationType] ?? 99;
    return priorityA - priorityB;
  });

  // Full view - show individual violations (when no consolidation needed)
  return (
    <div className="space-y-2">
      {sortedViolations.map((violation) => (
        <Alert
          key={violation.id}
          variant={getViolationVariant(violation.severity)}
          className="relative"
          showIcon={false}
        >
          <div className="flex items-start gap-3">
            {getViolationIcon(violation.severity)}
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <AlertTitle className="text-sm font-medium">
                  {getViolationTypeLabel(violation.violationType)}
                </AlertTitle>
                <Badge variant={getSeverityBadgeVariant(violation.severity)} className="text-xs">
                  {violation.severity.toUpperCase()}
                </Badge>
              </div>
              <AlertDescription className="text-sm">
                {violation.message}
                {violation.date && (
                  <span className="ml-2 text-muted-foreground">
                    ({new Date(violation.date).toLocaleDateString()})
                  </span>
                )}
              </AlertDescription>
              <div className="text-xs text-muted-foreground mt-1">
                {violation.violationType === 'missing-hours' ? (
                  <>
                    Expected: {violation.details.expected}h • 
                    Actual: {violation.details.actual}h • 
                    Minimum: {violation.details.threshold}h
                  </>
                ) : violation.violationType.includes('legal-') ? (
                  <>
                    Expected: {violation.details.expected}h • 
                    Actual: {violation.details.actual}h • 
                    Legal Limit: {violation.details.limit}h
                  </>
                ) : (
                  <>
                    Expected: {violation.details.expected}h • 
                    Actual: {violation.details.actual}h • 
                    Threshold: {violation.details.threshold}h
                  </>
                )}
              </div>
            </div>
          </div>
        </Alert>
      ))}
    </div>
  );
}

/**
 * PolicyViolationSummary Component
 * 
 * Shows a summary of violations grouped by type
 */
interface PolicyViolationSummaryProps {
  violations: PolicyViolation[];
}

export function PolicyViolationSummary({ violations }: PolicyViolationSummaryProps) {
  if (violations.length === 0) {
    return (
      <Alert showIcon={false}>
        <Info className="h-4 w-4" />
        <AlertTitle>No Policy Violations</AlertTitle>
        <AlertDescription>
          All timesheets are within expected hours and policy limits.
        </AlertDescription>
      </Alert>
    );
  }

  const summary = {
    total: violations.length,
    byType: {
      dailyOverage: violations.filter(v => v.violationType === 'daily-overage').length,
      weeklyOverage: violations.filter(v => v.violationType === 'weekly-overage').length,
      legalMaximumDaily: violations.filter(v => v.violationType === 'legal-daily-maximum').length,
      legalMaximumWeekly: violations.filter(v => v.violationType === 'legal-weekly-maximum').length,
      missingHours: violations.filter(v => v.violationType === 'missing-hours').length,
    },
    bySeverity: {
      error: violations.filter(v => v.severity === 'error').length,
      warning: violations.filter(v => v.severity === 'warning').length,
      info: violations.filter(v => v.severity === 'info').length,
    },
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {summary.bySeverity.error > 0 && (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <div>
                <div className="text-2xl font-bold">{summary.bySeverity.error}</div>
                <div className="text-sm">Errors</div>
              </div>
            </div>
          </div>
        )}
        {summary.bySeverity.warning > 0 && (
          <div className="rounded-lg border border-orange-500 bg-orange-50 p-4">
            <div className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="h-5 w-5" />
              <div>
                <div className="text-2xl font-bold">{summary.bySeverity.warning}</div>
                <div className="text-sm">Warnings</div>
              </div>
            </div>
          </div>
        )}
        {summary.bySeverity.info > 0 && (
          <div className="rounded-lg border border-blue-500 bg-blue-50 p-4">
            <div className="flex items-center gap-2 text-blue-700">
              <Info className="h-5 w-5" />
              <div>
                <div className="text-2xl font-bold">{summary.bySeverity.info}</div>
                <div className="text-sm">Info</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h4 className="text-sm font-medium">Violations by Type</h4>
        <div className="space-y-1 text-sm">
          {summary.byType.dailyOverage > 0 && (
            <div className="flex justify-between">
              <span>Daily Overage</span>
              <Badge variant="secondary">{summary.byType.dailyOverage}</Badge>
            </div>
          )}
          {summary.byType.weeklyOverage > 0 && (
            <div className="flex justify-between">
              <span>Weekly Overage</span>
              <Badge variant="secondary">{summary.byType.weeklyOverage}</Badge>
            </div>
          )}
          {summary.byType.legalMaximumDaily > 0 && (
            <div className="flex justify-between">
              <span>Legal Maximum (Daily)</span>
              <Badge variant="destructive">{summary.byType.legalMaximumDaily}</Badge>
            </div>
          )}
          {summary.byType.legalMaximumWeekly > 0 && (
            <div className="flex justify-between">
              <span>Legal Maximum (Weekly)</span>
              <Badge variant="destructive">{summary.byType.legalMaximumWeekly}</Badge>
            </div>
          )}
          {summary.byType.missingHours > 0 && (
            <div className="flex justify-between">
              <span>Missing Hours</span>
              <Badge variant="secondary">{summary.byType.missingHours}</Badge>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
